import cx from 'classnames';
import { SectionRecord } from 'models/api-response';
import { ColumnAlign } from 'models/ui';
import { Badge, BadgeDateTime, Box, ChartPermission, DeleteCell, MultiLine } from 'components/common';
import { ELSAccordion, ELSAccordionItem, ELSDataTable } from 'components/common/els';
import './edit.chart.scss';

export interface EditChartSectionProps {
  chartName: string;
  chartSubtitle?: string;
  chartTime: string;
  collapsibleGroups?: boolean;
  columnHeaders?: { title: string; content: string };
  defaultOpenGroups?: string[];
  deleteStatusDictionary: Map<string, boolean>;
  fragmentCreatorId: number;
  fullName: string;
  hideDelete?: boolean;
  id: string;
  isStatusBadgeActive: boolean;
  onRecordDelete: Function;
  records: SectionRecord[];
  status: string;
  useBlueBackground: boolean;
}

export interface RecordGroup {
  title: string;
  records: SectionRecord[];
}

export const groupRecordsByGroupTitle = (records: SectionRecord[]): RecordGroup[] => {
  const groups: RecordGroup[] = [];
  const indexByTitle = new Map<string, number>();
  records.forEach((record) => {
    const title = record.groupTitle || record.sectionTitle || '';
    if (!indexByTitle.has(title)) {
      indexByTitle.set(title, groups.length);
      groups.push({ title, records: [] });
    }
    groups[indexByTitle.get(title)].records.push(record);
  });
  return groups;
};

interface CellRenderers {
  renderTitle: (row) => JSX.Element;
  renderContent: (row) => JSX.Element;
  renderDelete: (row) => JSX.Element;
}

const renderSectionTable = (record: SectionRecord, index: number, columnHeaders: EditChartSectionProps['columnHeaders'], hideDelete: boolean, cells: CellRenderers) => {
  const tableHeading = `${record.parentSectionTitle || ''} ${record.sectionTitle || ''}`.trim();
  const elementKey = record.sectionTitle || index;
  const visibleRecords = (record.records ?? []).filter((row) => !row.isHidden);
  const effectiveColumnHeaders = record.useSectionTitleAsHeader ? undefined : record.columnHeaders ?? columnHeaders;
  const contentHeaderText = effectiveColumnHeaders?.content ?? record.contentSectionTitle;
  const titleHeaderText = effectiveColumnHeaders?.title ?? tableHeading;
  const titleHeader = record.hideSectionTitleHeader ? <div className="u-els-hide-visually">{titleHeaderText}</div> : <div className="header-title">{titleHeaderText}</div>;
  return (
    <div key={elementKey} className={cx('edit-chart-section__table', { 'edit-chart-section__table--custom-headers': !!effectiveColumnHeaders })}>
      <ELSDataTable data={visibleRecords}>
        <column header={titleHeader} field="title" customRender={cells.renderTitle} />
        <column
          header={contentHeaderText ? <div className="header-title">{contentHeaderText}</div> : <div className="u-els-hide-visually">Content</div>}
          field="content"
          align={ColumnAlign.Left}
          customRender={cells.renderContent}
        />
        {!hideDelete && <column header={<div className="u-els-hide-visually">Delete</div>} customRender={cells.renderDelete} />}
      </ELSDataTable>
    </div>
  );
};

const renderCollectionTable = (record: SectionRecord, index: number) => {
  const columns = record.tableColumns ?? [];
  const rows = record.tableRows ?? [];
  return (
    <div key={record.sectionTitle || index} className="edit-chart-section__collection">
      {record.sectionTitle && <h4 className="edit-chart-section__collection-title">{record.sectionTitle}</h4>}
      <table className={`edit-chart-section__collection-table edit-chart-section__collection-table--cols-${columns.length}`}>
        <thead>
          <tr>
            {columns.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex}>
              {columns.map((header, colIndex) => (
                <td key={header}>{cells[colIndex]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const renderCollapsibleGroups = (records: SectionRecord[], id: string, defaultOpenGroups: string[], renderTable: (record: SectionRecord, index: number) => JSX.Element) => (
  <div className="edit-chart-section__accordion">
    <ELSAccordion id={`edit-chart-accordion-${id}`}>
      {groupRecordsByGroupTitle(records).map((group, groupIndex) => (
        <ELSAccordionItem key={group.title} id={`edit-chart-accordion-${id}-${groupIndex}`} title={group.title} isExpanded={defaultOpenGroups.includes(group.title)}>
          {group.records.map(renderTable)}
        </ELSAccordionItem>
      ))}
    </ELSAccordion>
  </div>
);

const EditChartSection = (props: EditChartSectionProps) => {
  const {
    chartName,
    chartSubtitle,
    chartTime,
    collapsibleGroups,
    columnHeaders,
    defaultOpenGroups,
    fullName,
    records,
    fragmentCreatorId,
    deleteStatusDictionary,
    hideDelete,
    status,
    isStatusBadgeActive
  } = props;
  const { onRecordDelete } = props;

  const renderTitle = (row) => (
    <div className={cx({ 'row--deleted': !deleteStatusDictionary.get(row.id) })}>
      <MultiLine text={row.title} />
    </div>
  );

  const renderContent = (row) => (
    <div className={cx({ 'row--deleted': !deleteStatusDictionary.get(row.id) })}>{typeof row.content !== 'boolean' && <MultiLine text={row.content} />}</div>
  );

  const renderDelete = (row) => (
    <ChartPermission fragmentCreatorId={fragmentCreatorId}>
      {({ allowDeleteFragment }) => (
        <DeleteCell isActive={allowDeleteFragment && !row.preventDelete && deleteStatusDictionary.get(row.id)} onDeleteClick={() => onRecordDelete(row)} />
      )}
    </ChartPermission>
  );

  const renderTable = (record: SectionRecord, index: number) =>
    record.tableColumns
      ? renderCollectionTable(record, index)
      : renderSectionTable(record, index, columnHeaders, Boolean(hideDelete), { renderTitle, renderContent, renderDelete });

  return (
    <div className="edit-chart-section">
      <div>
        <h3 className="u-els-margin-top">{chartName}</h3>
        {chartSubtitle && <p className="edit-chart-section__subtitle">{chartSubtitle}</p>}
        {status && (
          <Badge
            className={cx('status-badge u-els-margin-right-1o2', {
              'u-els-background-color-secondary-on-dark': props.useBlueBackground,
              'status-badge--active': isStatusBadgeActive
            })}
          >
            {status}
          </Badge>
        )}
        <BadgeDateTime className="u-els-margin-top" isoTime={chartTime} />
        <p className="u-els-color-n7 u-els-italic">{`Entered by: ${fullName}`}</p>
      </div>

      {!collapsibleGroups && <div className="sidebar__divider" />}
      <Box pb2>{collapsibleGroups ? renderCollapsibleGroups(records, props.id, defaultOpenGroups ?? [], renderTable) : records.map(renderTable)}</Box>
    </div>
  );
};

EditChartSection.displayName = 'EditChartSection';
export default EditChartSection;
